import type * as THREE from 'three';

// Absolute, not relative to the model: parts a hair apart in a large assembly
// must stay apart. Twins OCCT writes for a shared edge match to the bit.
const WELD_TOLERANCE = 1e-6;
const ID_STRIDE = 2 ** 26;

const EMPTY = -1;
const INITIAL_SLOTS = 64;
const UINT32_RANGE = 2 ** 32;

// XOR keeps the low 32 bits of each operand, so a coordinate past int32 still
// hashes by both of its halves.
function mix(hash: number, value: number): number {
  const low = Math.imul(hash ^ Math.trunc(value), 0x9e3779b1);
  const high = Math.imul(low ^ Math.trunc(value / UINT32_RANGE), 0x85ebca6b);
  return high ^ (high >>> 15);
}

export class VertexWelder {
  private cells = new Float64Array(INITIAL_SLOTS * 3);
  private slots = new Int32Array(INITIAL_SLOTS).fill(EMPTY);
  private size = 0;

  id(x: number, y: number, z: number): number {
    const cx = Math.round(x / WELD_TOLERANCE);
    const cy = Math.round(y / WELD_TOLERANCE);
    const cz = Math.round(z / WELD_TOLERANCE);
    const mask = this.slots.length - 1;
    let slot = mix(mix(mix(0, cx), cy), cz) & mask;
    for (;;) {
      const id = this.slots[slot];
      if (id === EMPTY) break;
      if (this.cells[id * 3] === cx && this.cells[id * 3 + 1] === cy && this.cells[id * 3 + 2] === cz) return id;
      slot = (slot + 1) & mask;
    }
    const id = this.size++;
    this.slots[slot] = id;
    this.cells[id * 3] = cx;
    this.cells[id * 3 + 1] = cy;
    this.cells[id * 3 + 2] = cz;
    if (this.size * 2 > this.slots.length) this.grow();
    return id;
  }

  idOf(point: THREE.Vector3): number {
    return this.id(point.x, point.y, point.z);
  }

  /** Where the point numbered `id` lies, to within the weld tolerance. */
  positionOf(id: number, target: THREE.Vector3): THREE.Vector3 {
    return target.set(this.cells[id * 3], this.cells[id * 3 + 1], this.cells[id * 3 + 2]).multiplyScalar(WELD_TOLERANCE);
  }

  private grow(): void {
    const cells = new Float64Array(this.cells.length * 2);
    cells.set(this.cells);
    this.cells = cells;
    const slots = new Int32Array(this.slots.length * 2).fill(EMPTY);
    const mask = slots.length - 1;
    for (let id = 0; id < this.size; id++) {
      let slot = mix(mix(mix(0, cells[id * 3]), cells[id * 3 + 1]), cells[id * 3 + 2]) & mask;
      while (slots[slot] !== EMPTY) slot = (slot + 1) & mask;
      slots[slot] = id;
    }
    this.slots = slots;
  }
}

export function edgeKey(u: number, v: number): number {
  return u < v ? u * ID_STRIDE + v : v * ID_STRIDE + u;
}

export function edgeEnds(key: number): [number, number] {
  return [Math.floor(key / ID_STRIDE), key % ID_STRIDE];
}

export function countEdge(uses: Map<number, number>, u: number, v: number): void {
  if (u === v) return;
  const key = edgeKey(u, v);
  uses.set(key, (uses.get(key) ?? 0) + 1);
}

export class UnionFind {
  private readonly parent: Uint32Array;

  constructor(size: number) {
    this.parent = new Uint32Array(size).map((_, i) => i);
  }

  find(item: number): number {
    let root = item;
    while (this.parent[root] !== root) root = this.parent[root];
    this.parent[item] = root;
    return root;
  }

  union(a: number, b: number): void {
    const rootA = this.find(a);
    const rootB = this.find(b);
    if (rootA !== rootB) this.parent[rootB] = rootA;
  }
}
